import { registerApiInjection, getBaseApi, isStoreRegistered, } from './storeRegistry';
/**
 * Creates an entity API instance that automatically injects endpoints.
 * Returns a proxy that forwards all property access to the injected API.
 *
 * @param config - Configuration with tagTypes and endpoint definitions
 * @returns A proxy object that behaves like the injected API instance
 *
 * @example
 * ```typescript
 * export const faqApi = createEntityApi({
 *   tagTypes: ['FAQS'],
 *   endpoints: faqEndpoints,
 * });
 *
 * export const { useGetFaqsQuery, usePostFaqMutation } = faqApi;
 * ```
 */
export function createEntityApi(config) {
    let injectedApi = null;
    const ensureInjected = () => {
        if (injectedApi) {
            return injectedApi;
        }
        if (!isStoreRegistered()) {
            throw new Error('Store not registered. Entity API cannot be used before store initialization.');
        }
        const baseApi = getBaseApi();
        injectedApi = baseApi
            .enhanceEndpoints({ addTagTypes: config.tagTypes })
            .injectEndpoints({
            endpoints: (builder) => config.endpoints(builder),
        });
        return injectedApi;
    };
    registerApiInjection((api) => {
        injectedApi = api
            .enhanceEndpoints({ addTagTypes: config.tagTypes })
            .injectEndpoints({
            endpoints: (builder) => config.endpoints(builder),
        });
    });
    if (isStoreRegistered()) {
        ensureInjected();
    }
    // Return proxy that forwards all property access to injected API
    // Using 'any' for the proxy target because hooks are dynamically added by RTK Query
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return new Proxy({}, {
        get(_target, prop) {
            const api = ensureInjected();
            return api[prop];
        },
    });
}
